import React from 'react';
import { useDispatch } from 'react-redux';
import { type GridColDef } from '@mui/x-data-grid-premium';

import { Icon, showDialogGenericInfo, useSettings } from 'app/main/components';
import { ViewTable, ViewTableUtils } from 'app/main/components/page-layouts';

import { showDemsRecipientsOpenDetailsDialog } from './DemsRecipientsOpenDetailsDialog';
import { showDemsRecipientsClickDetailsDialog } from './DemsRecipientsClickDetailsDialog';

interface DemsRecipientsExternalTabProps {
	itemUid: string;
}

export default function DemsRecipientsExternalTab({ itemUid }: DemsRecipientsExternalTabProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	const [refresh, setRefresh] = React.useState(false);
	const updateRefresh = () => setRefresh(state => !state);

	function showBounceDialog(bounceType) {
		dispatch(
			showDialogGenericInfo({
				title: t(`DIALOG_INFO_${bounceType}_TITLE`),
				content: t(`DIALOG_INFO_${bounceType}_TEXT`)
			})
		);
	}

	const columns: GridColDef[] = [
		{
			field: 'dems_title',
			headerName: t('DEMS_RECIPIENTS_COLUMN_DEM'),
			minWidth: 200,
			flex: 2,
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderCellWithTooltip({ value, to: { module: 'dems', uid: row.dems_uid } }),
			hideable: false
		},
		{
			field: 'dmst_title',
			headerName: t('DEMS_RECIPIENTS_COLUMN_STATUS'),
			minWidth: 120,
			renderCell: ({ value, row }) => ViewTableUtils.renderSquareChip({ value, backgroundColor: row.dmst_color })
		},
		{
			field: 'dems_date',
			headerName: t('DEMS_RECIPIENTS_COLUMN_DATE'),
			minWidth: 120,
			renderCell: ({ value }) =>
				ViewTableUtils.renderDate({ value, format: { year: 'numeric', month: '2-digit', day: '2-digit' } })
		},
		{
			field: 'mail_title',
			headerName: t('DEMS_RECIPIENTS_COLUMN_MAIL'),
			minWidth: 200,
			flex: 2,
			renderCell: ViewTableUtils.renderCellWithTooltip
		},
		{
			field: 'derc_clicks_count',
			type: 'number',
			headerName: t('DEMS_RECIPIENTS_COLUMN_CLICKS'),
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderSquareChip({
					value: `${value || 0}`,
					backgroundColor: theme => theme.palette.success.main,
					icon: <Icon icon="cursor-default-click-outline" size={18} />,
					onClick:
						(row.derc_clicks || []).length > 0
							? () => dispatch(showDemsRecipientsClickDetailsDialog({ clicks: row.derc_clicks }))
							: undefined,
					disabled: !value
				})
		},
		{
			field: 'derc_open_count',
			type: 'number',
			headerName: t('DEMS_RECIPIENTS_COLUMN_OPENS'),
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderSquareChip({
					value: `${value || 0}`,
					backgroundColor: theme => theme.palette.warning.main,
					icon: <Icon icon="eye" size={18} />,
					onClick:
						(row.derc_opens || []).length > 0
							? () => dispatch(showDemsRecipientsOpenDetailsDialog({ opens: row.derc_opens }))
							: undefined,
					disabled: !value
				})
		},
		{
			field: 'mail_unsubscribed_dems_uid',
			type: 'number',
			headerName: t('DEMS_RECIPIENTS_COLUMN_UNSUBSCRIBED'),
			renderCell: ({ value, row }) =>
				value === row.dems_uid &&
				ViewTableUtils.renderSquareChip({
					value: t('DEMS_RECIPIENTS_COLUMN_UNSUBSCRIBED'),
					backgroundColor: theme => theme.palette.error.main
					// icon: <Icon icon="email-off" size={18} />
				})
		},
		{
			field: 'derc_bounce_type',
			type: 'number',
			headerName: t('DEMS_RECIPIENTS_COLUMN_BOUNCE'),
			renderCell: ({ value }) =>
				ViewTableUtils.renderSquareChip({
					value,
					icon: <Icon icon="alert" size={18} />,
					onClick: () => showBounceDialog(value)
				})
		}
	];

	return (
		<ViewTable
			module="dems_recipients"
			columns={columns}
			refresh={refresh}
			updateRefresh={updateRefresh}
			defaultFilters={[
				{ filt_field: 'dems_deleted', filt_clause: 'not_equal', filt_value: 1 },
				{ filt_field: 'mail_table_uid', filt_clause: 'equals', filt_value: itemUid }
			]}
			showExportButton={false}
		/>
	);
}
